import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  OnInit,
  computed,
  inject,
  input,
  signal
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';

import { SessionStatus, SessionStatusLabel } from '@core/models/brijjsports.enums';
import { ArrowValue, ScoringSession } from '@core/models/brijjsports.models';
import { ToastService } from '@core/services/toast.service';
import { BrijjSportsStateService } from '@core/state/brijjsports-state.service';
import {
  archerSessionTotal,
  arrowToInt,
  arrowsShot,
  endTotal,
  maxPossible,
  sortEndDescending,
  tenCount,
  totalArrows,
  xCount
} from '@core/utils/scoring.util';

import { PAD_VALUES } from '../models/coach-scoring-live.state';
import { CoachScoringLiveService } from '../services/coach-scoring-live.service';

@Component({
  selector: 'app-coach-scoring-live-page',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatTabsModule, MatTooltipModule],
  templateUrl: './coach-scoring-live-page.component.html',
  styleUrl: './coach-scoring-live-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CoachScoringLivePageComponent implements OnInit {
  /** Route param. */
  readonly sessionId = input.required<string>();

  protected readonly state = inject(BrijjSportsStateService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly liveService = inject(CoachScoringLiveService);

  protected readonly padValues = PAD_VALUES;
  protected readonly statusLabel = SessionStatusLabel;

  protected statusLabelFor(s: ScoringSession): string {
    return SessionStatusLabel[s.status ?? SessionStatus.Idle];
  }

  protected statusClassFor(s: ScoringSession): string {
    return `status status--${s.status ?? SessionStatus.Idle}`;
  }

  protected readonly session = computed<ScoringSession | null>(() => this.state.sessionById(this.sessionId()) ?? null);

  protected readonly archers = computed(() =>
    (this.session()?.participantIds ?? [])
      .map(id => this.state.athleteById(id))
      .filter((a): a is NonNullable<typeof a> => !!a)
  );

  /** Currently selected archer tab. */
  protected readonly activeIndex = signal(0);
  protected readonly activeRound = signal(0);
  /** When set, the next pad press replaces this arrow instead of appending. */
  protected readonly editTarget = signal<{ roundIndex: number; endIndex: number; arrowIndex: number } | null>(null);

  protected readonly currentArcherId = computed<string | null>(() => this.archers()[this.activeIndex()]?.id ?? null);

  protected readonly compoundInner10 = computed(() => this.session()?.compoundInner10 ?? false);

  protected readonly currentScore = computed(() => {
    const session = this.session();
    const archerId = this.currentArcherId();
    if (!session || !archerId) return null;
    return session.scores?.find(s => s.athleteId === archerId) ?? null;
  });

  protected readonly currentRoundConfig = computed(() => {
    return this.session()?.rounds?.[this.activeRound()] ?? null;
  });

  protected readonly currentRoundEnds = computed<ArrowValue[][]>(() => {
    return this.currentScore()?.rounds[this.activeRound()]?.ends ?? [];
  });

  protected readonly currentEndIndex = computed(() => {
    const cfg = this.currentRoundConfig();
    if (!cfg) return 0;
    const ends = this.currentRoundEnds();
    for (let i = 0; i < cfg.endsCount; i++) {
      if ((ends[i]?.length ?? 0) < cfg.arrowsPerEnd) return i;
    }
    return cfg.endsCount - 1;
  });

  protected readonly endTotals = computed(() => {
    const compound = this.compoundInner10();
    return this.currentRoundEnds().map(end => endTotal(end, compound));
  });

  protected readonly grandTotal = computed(() => {
    const score = this.currentScore();
    if (!score) return 0;
    return archerSessionTotal(score, this.compoundInner10());
  });

  protected readonly arrowsShotCount = computed(() => (this.currentScore() ? arrowsShot(this.currentScore()!) : 0));

  protected readonly maxArrowsTotal = computed(() => totalArrows(this.session()?.rounds ?? []));

  protected readonly maxPointsPossible = computed(() =>
    maxPossible(this.session()?.rounds ?? [], this.compoundInner10())
  );

  protected readonly xs = computed(() => (this.currentScore() ? xCount(this.currentScore()!) : 0));
  protected readonly tens = computed(() => (this.currentScore() ? tenCount(this.currentScore()!) : 0));

  protected readonly target = computed(() => this.state.athleteById(this.currentArcherId() ?? '')?.targetScore ?? null);

  protected readonly targetPct = computed(() => {
    const t = this.target();
    if (!t || t <= 0) return null;
    return Math.min(100, Math.round((this.grandTotal() / t) * 100));
  });

  protected readonly isLive = computed(() => this.session()?.status === SessionStatus.Active);
  protected readonly isPaused = computed(() => this.session()?.status === SessionStatus.Paused);
  protected readonly isCompleted = computed(() => this.session()?.status === SessionStatus.Completed);

  ngOnInit(): void {
    if (!this.session()) this.router.navigate(['/coach/scoring']);
  }

  selectArcher(index: number): void {
    this.activeIndex.set(index);
    this.activeRound.set(0);
    this.editTarget.set(null);
  }

  selectRound(index: number): void {
    this.activeRound.set(index);
    this.editTarget.set(null);
  }

  recordArrow(value: ArrowValue): void {
    if (!this.isLive()) return;
    const archerId = this.currentArcherId();
    const session = this.session();
    if (!archerId || !session) return;
    const cfg = this.currentRoundConfig();
    if (!cfg) return;

    const existing = this.currentRoundEnds().map(e => [...e]);
    const edit = this.editTarget();

    let nextEnds: ArrowValue[][];
    if (edit) {
      const target = existing[edit.endIndex] ?? [];
      target[edit.arrowIndex] = value;
      existing[edit.endIndex] = target;
      nextEnds = existing;
      this.editTarget.set(null);
    } else {
      while (existing.length < cfg.endsCount) existing.push([]);
      const idx = this.currentEndIndex();
      if ((existing[idx]?.length ?? 0) >= cfg.arrowsPerEnd) return;
      existing[idx] = [...(existing[idx] ?? []), value];
      nextEnds = existing;
    }

    this.liveService.setEnds({
      sessionId: session.id,
      athleteId: archerId,
      roundIndex: this.activeRound(),
      ends: nextEnds
    });

    const justFilled = nextEnds.length === cfg.endsCount && nextEnds.every(e => e.length === cfg.arrowsPerEnd);
    if (justFilled) {
      const score = this.state.sessionById(session.id)?.scores?.find(s => s.athleteId === archerId);
      if (score) {
        const archer = this.state.athleteById(archerId);
        this.toast.success(
          score.pbr ? 'Round complete · PB!' : 'Round complete',
          `${archer?.name ?? ''}: ${score.total}${this.target() ? ' / ' + this.target() : ''}`
        );
      }
    }
  }

  undoLast(): void {
    if (!this.isLive()) return;
    const session = this.session();
    const archerId = this.currentArcherId();
    if (!session || !archerId) return;
    const ends = this.currentRoundEnds().map(e => [...e]);
    for (let i = ends.length - 1; i >= 0; i--) {
      if (ends[i].length > 0) {
        ends[i].pop();
        this.liveService.setEnds({ sessionId: session.id, athleteId: archerId, roundIndex: this.activeRound(), ends });
        return;
      }
    }
  }

  beginEdit(roundIndex: number, endIndex: number, arrowIndex: number): void {
    if (!this.isLive()) return;
    if (this.activeRound() !== roundIndex) this.selectRound(roundIndex);
    this.editTarget.set({ roundIndex, endIndex, arrowIndex });
    this.toast.info('Editing arrow', 'Tap a value to replace.');
  }

  cancelEdit(): void {
    this.editTarget.set(null);
  }

  autoSortEnd(endIndex: number): void {
    if (!this.isLive()) return;
    const session = this.session();
    const archerId = this.currentArcherId();
    if (!session || !archerId) return;
    const ends = this.currentRoundEnds().map(e => [...e]);
    if (!ends[endIndex]?.length) return;
    ends[endIndex] = sortEndDescending(ends[endIndex]);
    this.liveService.setEnds({ sessionId: session.id, athleteId: archerId, roundIndex: this.activeRound(), ends });
  }

  resetCurrent(): void {
    const archerId = this.currentArcherId();
    const session = this.session();
    if (!archerId || !session) return;
    this.liveService.confirmAndResetArcher({ sessionId: session.id, athleteId: archerId });
  }

  pause(): void {
    const id = this.session()?.id;
    if (id) this.liveService.pause({ sessionId: id });
  }

  resume(): void {
    const id = this.session()?.id;
    if (id) this.liveService.resume({ sessionId: id });
  }

  end(): void {
    const session = this.session();
    if (!session) return;
    this.liveService.confirmAndEnd({ sessionId: session.id }, session.name);
  }

  async archive(): Promise<void> {
    const session = this.session();
    if (!session) return;
    const ok = await this.liveService.confirmAndArchive({ sessionId: session.id });
    if (ok) this.router.navigate(['/coach/scoring']);
  }

  arrowDisplay(value: ArrowValue): string {
    return value === 'M' ? 'M' : value === 'X' ? 'X' : String(value);
  }

  arrowClass(value: ArrowValue): string {
    if (value === 'X') return 'arrow arrow--x';
    if (value === 'M') return 'arrow arrow--m';
    const n = arrowToInt(value);
    if (n >= 9) return 'arrow arrow--high';
    if (n >= 7) return 'arrow arrow--mid';
    return 'arrow arrow--low';
  }

  isEditingArrow(roundIndex: number, endIndex: number, arrowIndex: number): boolean {
    const e = this.editTarget();
    return !!e && e.roundIndex === roundIndex && e.endIndex === endIndex && e.arrowIndex === arrowIndex;
  }

  @HostListener('window:keydown', ['$event'])
  onKey(ev: KeyboardEvent): void {
    if (!this.isLive()) return;
    if (ev.metaKey || ev.ctrlKey || ev.altKey) return;
    const tag = (ev.target as HTMLElement | null)?.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea') return;
    const key = ev.key;
    if (key === 'x' || key === 'X') {
      ev.preventDefault();
      this.recordArrow('X');
      return;
    }
    if (key === 'm' || key === 'M') {
      ev.preventDefault();
      this.recordArrow('M');
      return;
    }
    if (key === 'u' || key === 'U' || key === 'Backspace') {
      ev.preventDefault();
      this.undoLast();
      return;
    }
    if (key === '0') {
      ev.preventDefault();
      this.recordArrow(10);
      return;
    }
    const n = Number(key);
    if (Number.isInteger(n) && n >= 1 && n <= 9) {
      ev.preventDefault();
      this.recordArrow(n);
    }
  }
}
